import React, { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Blog,
  BlogCategory,
  createBlog,
  updateBlog,
  deleteBlog,
  searchBlogs,
  getRelatedBlogs,
  createBlogCategory,
  updateBlogCategory,
  deleteBlogCategory,
  incrementBlogViews,
  generateSlug,
  moveBlogUp,
  moveBlogDown,
} from "../firebase/blogService";
import { Timestamp } from "firebase/firestore";
import { blogUpdateLimiter, viewIncrementLimiter } from "../utils/rateLimiter";
import { blogKeys } from "../hooks/useOpenSourceQuery";
import {
  blogsQuery,
  featuredBlogsQuery,
  blogCategoriesQuery,
} from "../queries/contentQueries";

interface BlogContextType {
  blogs: Blog[];
  featuredBlogs: Blog[];
  blogCategories: BlogCategory[];
  loading: boolean;
  error: Error | null;
  refreshBlogs: () => Promise<void>;
  refreshBlogCategories: () => Promise<void>;
  addBlog: (
    blog: Omit<Blog, "id" | "createdAt" | "updatedAt">
  ) => Promise<string>;
  editBlog: (
    id: string,
    blog: Partial<Omit<Blog, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removeBlog: (id: string) => Promise<void>;
  searchBlogPosts: (
    searchTerm: string,
    filters?: {
      category?: string;
      language?: "Bengali" | "English";
      difficulty?: "Beginner" | "Intermediate" | "Advanced";
    }
  ) => Promise<Blog[]>;
  getRelatedBlogPosts: (blogId: string, limit?: number) => Promise<Blog[]>;
  incrementViews: (id: string) => Promise<void>;
  addBlogCategory: (category: Omit<BlogCategory, "id">) => Promise<string>;
  editBlogCategory: (
    id: string,
    category: Partial<Omit<BlogCategory, "id">>
  ) => Promise<void>;
  removeBlogCategory: (id: string) => Promise<void>;
  moveUp: (id: string) => Promise<void>;
  moveDown: (id: string) => Promise<void>;
  generateBlogSlug: (title: string) => string;
}

const EMPTY_BLOGS: Blog[] = [];
const EMPTY_CATEGORIES: BlogCategory[] = [];

// Data lives in the TanStack Query cache; the provider is kept so the app tree doesn't change.
export const BlogProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => <>{children}</>;

export const useBlogs = (): BlogContextType => {
  const queryClient = useQueryClient();
  const blogs = useQuery(blogsQuery);
  const featured = useQuery(featuredBlogsQuery);
  const categories = useQuery(blogCategoriesQuery);

  const setBlogs = (update: (prev: Blog[]) => Blog[]) =>
    queryClient.setQueryData<Blog[]>(blogsQuery.queryKey, (prev) =>
      update(prev ?? [])
    );

  const setBlogCategories = (
    update: (prev: BlogCategory[]) => BlogCategory[]
  ) =>
    queryClient.setQueryData<BlogCategory[]>(
      blogCategoriesQuery.queryKey,
      (prev) => update(prev ?? [])
    );

  // Featured list is a separate Firestore query; let it refetch after writes
  const invalidateFeatured = () =>
    queryClient.invalidateQueries({ queryKey: featuredBlogsQuery.queryKey });

  const refreshBlogs = async () => {
    await queryClient.refetchQueries({ queryKey: blogKeys.lists() });
    await queryClient.refetchQueries({ queryKey: featuredBlogsQuery.queryKey });
  };

  const refreshBlogCategories = async () => {
    await categories.refetch();
  };

  const addBlog = async (
    blog: Omit<Blog, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    const id = await createBlog(blog);
    setBlogs((prev) => [
      ...prev,
      { ...blog, id, createdAt: Timestamp.now(), updatedAt: Timestamp.now() },
    ]);
    invalidateFeatured();
    return id;
  };

  const editBlog = async (
    id: string,
    blog: Partial<Omit<Blog, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    // Rate limiting for blog updates
    if (!blogUpdateLimiter.isAllowed(`edit-${id}`)) {
      throw new Error(
        "Too many update requests. Please wait a moment before trying again."
      );
    }

    await updateBlog(id, blog);
    setBlogs((prev) =>
      prev.map((b) =>
        b.id === id
          ? ({ ...b, ...blog, updatedAt: Timestamp.now() } as Blog)
          : b
      )
    );
    invalidateFeatured();
  };

  const removeBlog = async (id: string): Promise<void> => {
    await deleteBlog(id);
    setBlogs((prev) => prev.filter((b) => b.id !== id));
    queryClient.setQueryData<Blog[]>(featuredBlogsQuery.queryKey, (prev) =>
      prev?.filter((b) => b.id !== id)
    );
  };

  const searchBlogPosts = async (
    searchTerm: string,
    filters?: {
      category?: string;
      language?: "Bengali" | "English";
      difficulty?: "Beginner" | "Intermediate" | "Advanced";
    }
  ): Promise<Blog[]> => {
    return await searchBlogs(searchTerm, filters);
  };

  const getRelatedBlogPosts = async (
    blogId: string,
    limit?: number
  ): Promise<Blog[]> => {
    return await getRelatedBlogs(blogId, limit);
  };

  const incrementViews = async (id: string): Promise<void> => {
    // Rate limiting for view increments to prevent spam
    if (!viewIncrementLimiter.isAllowed(`view-${id}`)) {
      // Silently skip if rate limited (views aren't critical)
      return;
    }

    try {
      await incrementBlogViews(id);
      setBlogs((prev) =>
        prev.map((b) => (b.id === id ? { ...b, views: (b.views || 0) + 1 } : b))
      );
    } catch (error) {
      // Silently handle view increment errors to not disrupt user experience
      console.warn("Failed to increment views:", error);
    }
  };

  const addBlogCategory = async (
    category: Omit<BlogCategory, "id">
  ): Promise<string> => {
    const id = await createBlogCategory(category);
    setBlogCategories((prev) => [...prev, { ...category, id }]);
    return id;
  };

  const editBlogCategory = async (
    id: string,
    category: Partial<Omit<BlogCategory, "id">>
  ): Promise<void> => {
    await updateBlogCategory(id, category);
    setBlogCategories((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...category } : c))
    );
  };

  const removeBlogCategory = async (id: string): Promise<void> => {
    await deleteBlogCategory(id);
    setBlogCategories((prev) => prev.filter((c) => c.id !== id));
  };

  // Move operations change ordering server-side, so refetch
  const moveUp = async (id: string): Promise<void> => {
    await moveBlogUp(id);
    await refreshBlogs();
  };

  const moveDown = async (id: string): Promise<void> => {
    await moveBlogDown(id);
    await refreshBlogs();
  };

  const generateBlogSlug = (title: string): string => {
    return generateSlug(title);
  };

  return {
    blogs: blogs.data ?? EMPTY_BLOGS,
    featuredBlogs: featured.data ?? EMPTY_BLOGS,
    blogCategories: categories.data ?? EMPTY_CATEGORIES,
    loading: blogs.isPending || featured.isPending,
    error: (blogs.error ?? featured.error ?? categories.error) as Error | null,
    refreshBlogs,
    refreshBlogCategories,
    addBlog,
    editBlog,
    removeBlog,
    searchBlogPosts,
    getRelatedBlogPosts,
    incrementViews,
    addBlogCategory,
    editBlogCategory,
    removeBlogCategory,
    moveUp,
    moveDown,
    generateBlogSlug,
  };
};
